import { BellOff, BellRing } from 'lucide-react';
import CircularProgress from '@/components/common/CircularProgress';
import { Button } from '@/components/ui/button';
import logoPsi from '../assets/logoPsiMammoliti.svg';
import { isPhoneDeviceNow } from './isMobileDevice';
import { detectOs } from './pushDevice';

interface Props {
  /** `needs_enable`: falta activar. `denied`: el sistema tiene el permiso bloqueado. */
  variant: 'needs_enable' | 'denied';
  working: boolean;
  error: string | null;
  onEnable: () => void;
  onRecheck: () => void;
  onSignOut: () => void;
}

/**
 * Pasos para reactivar el permiso, por plataforma. Una web/PWA NO puede abrir los ajustes del sistema ni volver a pedir el
 * permiso una vez denegado: lo único posible es explicar el camino. (Los textos del sistema pueden variar según la versión.)
 */
const DENIED_STEPS: Record<'ios' | 'android' | 'other', string[]> = {
  ios: [
    'Abrí la app Ajustes del iPhone.',
    'Tocá Notificaciones y buscá "Psi Platform" en la lista.',
    'Activá "Permitir notificaciones".',
    'Volvé a esta app y tocá "Ya lo activé".',
  ],
  android: [
    'Mantené apretado el ícono de Psi Platform y tocá "Información de la app".',
    'Entrá a "Notificaciones" y activá "Permitir notificaciones".',
    'Si no aparece esa opción: abrí Chrome → menú ⋮ → Configuración → Configuración del sitio → Notificaciones, y permití este sitio.',
    'Volvé a esta app y tocá "Ya lo activé".',
  ],
  other: [
    'Abrí los ajustes de tu dispositivo o del navegador y buscá los permisos de notificaciones de esta app.',
    'Permití las notificaciones.',
    'Volvé a esta app y tocá "Ya lo activé".',
  ],
};

/**
 * Pantalla que reemplaza a toda la app mientras las notificaciones no están activadas: sin barra inferior ni campanita.
 * Es independiente de Perfil → Notificaciones a propósito (esa pantalla también queda bloqueada). Es un bloqueo de UX,
 * no de seguridad.
 */
export function PwaPushGateScreen({ variant, working, error, onEnable, onRecheck, onSignOut }: Props) {
  const isPhone = isPhoneDeviceNow();
  const os = detectOs();
  // Una tablet iOS es un iPad, no un iPhone.
  const steps = os === 'ios' && !isPhone ? ['Abrí la app Ajustes del iPad.', ...DENIED_STEPS.ios.slice(1)] : DENIED_STEPS[os];

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[#F3EFFF] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] font-[Poppins,sans-serif]">
      <header className="flex items-center gap-2.5 px-4 py-3">
        <img src={logoPsi} alt="" aria-hidden width={32} height={32} />
        <span className="text-base font-bold text-[#6D57BC]">Psi Platform</span>
      </header>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-5 px-5 py-6 text-left">
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white text-[#6D57BC] shadow-sm" aria-hidden>
          {variant === 'denied' ? <BellOff size={28} /> : <BellRing size={28} />}
        </div>

        {variant === 'needs_enable' ? (
          <>
            <div className="flex flex-col gap-2">
              <h1 className="m-0 text-2xl font-semibold text-[#2d2049]">Activá las notificaciones para usar la app</h1>
              <p className="m-0 text-sm text-[#455375]">
                Las usamos para avisarte al instante de tus sesiones y de lo que pasa en Psi Mammoliti. Sin ellas no podés ver el resto de la app.
              </p>
            </div>
            <Button variant="primary" size="default" className="w-full" onClick={onEnable} disabled={working}>
              {working ? 'Activando…' : 'Activar notificaciones'}
            </Button>
            <p className="m-0 text-xs text-[#455375]">
              {isPhone
                ? 'Se activan en este celular. Si ya usás Psi Platform en otro celular de la misma marca (otro iPhone u otro Android), esto desactiva las notificaciones de ese otro.'
                : 'Se activan en este dispositivo. Las notificaciones de tu celular no se ven afectadas.'}
            </p>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <h1 className="m-0 text-2xl font-semibold text-[#2d2049]">Reactivá las notificaciones</h1>
              <p className="m-0 text-sm text-[#455375]">
                Tenés las notificaciones bloqueadas para esta app. Para seguir usándola hay que habilitarlas desde los ajustes de tu
                dispositivo. No podemos abrir los ajustes por vos, pero estos son los pasos:
              </p>
            </div>
            <ol className="m-0 flex list-decimal flex-col gap-2 pl-5 text-sm text-[#2d2049]">
              {steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <Button variant="primary" size="default" className="w-full" onClick={onRecheck} disabled={working}>
              {working ? 'Revisando…' : 'Ya lo activé'}
            </Button>
          </>
        )}

        {error && (
          <p role="alert" className="m-0 text-sm text-coral">
            {error}
          </p>
        )}

        <Button variant="ghost" size="sm" className="self-start text-[#455375]" onClick={onSignOut}>
          Cerrar sesión
        </Button>
      </main>
    </div>
  );
}

/** Splash mientras se revisa el estado la primera vez (con tope: ver `GATE_CHECK_TIMEOUT_MS`). */
export function PwaPushGateChecking() {
  return (
    <div className="grid min-h-[100dvh] place-items-center bg-[#F3EFFF]">
      <CircularProgress sizeRem={2.25} label="Cargando" vertical />
    </div>
  );
}
